import { useState, useEffect, useCallback } from 'react';

// ─── Types ───────────────────────────────────────────────────────────────────

export type DeviceType = 'ios' | 'android' | 'desktop';

export type BrowserType =
  | 'safari'
  | 'safari-26'      // iOS 26+ Safari (new UI)
  | 'chrome'
  | 'firefox'
  | 'edge'
  | 'samsung'
  | 'opera'
  | 'inapp-facebook'
  | 'inapp-instagram'
  | 'inapp-twitter'
  | 'inapp-linkedin'
  | 'inapp-threads'
  | 'unknown';

export type InstallMethod =
  | 'native-prompt'        // Desktop Chrome/Edge beforeinstallprompt
  | 'ios-safari'           // iOS Safari share → Add to Home Screen
  | 'ios-safari-26'        // iOS 26 Safari new flow
  | 'ios-chrome'           // iOS Chrome ⋯ → Add to Home Screen
  | 'android-chrome'       // Android Chrome ⋮ → Add to Home Screen
  | 'android-edge'         // Android Edge ⋯ → Add to Home Screen
  | 'android-firefox'      // Android Firefox ⋮ → Add to Home Screen
  | 'desktop-safari'       // Desktop Safari File → Add to Dock
  | 'open-in-browser'      // In-app browser → open in system browser first
  | 'not-supported';

export interface DeviceDetectionResult {
  device: DeviceType;
  browser: BrowserType;
  isStandalone: boolean;
  isIPad: boolean;
  installMethod: InstallMethod;
  canInstall: boolean;
  /** For Desktop Chrome/Edge — triggers the native install prompt */
  triggerNativeInstall: () => Promise<boolean>;
  /** Whether the native beforeinstallprompt event has been captured */
  hasNativePrompt: boolean;
}

// ─── LocalStorage key for "dismissed" tracking ───────────────────────────────

const STORAGE_KEY = 'atlas-a2hs-dismissed';
const STORAGE_COUNT_KEY = 'atlas-a2hs-count';

export function getA2HSDismissedTimestamp(): number | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v ? parseInt(v, 10) : null;
  } catch {
    return null;
  }
}

export function setA2HSDismissed(): void {
  try {
    localStorage.setItem(STORAGE_KEY, Date.now().toString());
    const count = getA2HSDisplayCount();
    localStorage.setItem(STORAGE_COUNT_KEY, (count + 1).toString());
  } catch {}
}

export function getA2HSDisplayCount(): number {
  try {
    const v = localStorage.getItem(STORAGE_COUNT_KEY);
    return v ? parseInt(v, 10) : 0;
  } catch {
    return 0;
  }
}

export function clearA2HSDismissed(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_COUNT_KEY);
  } catch {}
}

/**
 * Returns true if the prompt should be shown based on timing rules:
 * - First 3 dismissals: show again after 2 days
 * - After that: show again after 5 days
 * - Max 5 total shows
 */
export function shouldShowA2HSPrompt(): boolean {
  const count = getA2HSDisplayCount();
  if (count >= 5) return false;

  const ts = getA2HSDismissedTimestamp();
  if (!ts) return true; // never dismissed

  const daysSince = (Date.now() - ts) / (1000 * 60 * 60 * 24);
  if (count <= 3) return daysSince >= 2;
  return daysSince >= 5;
}

// ─── Detection helpers (adapted from philfung/add-to-homescreen) ─────────────

function matchUA(regex: RegExp): boolean {
  return regex.test(navigator.userAgent);
}

function isDeviceIOS(): boolean {
  return (
    matchUA(/iPhone|iPad|iPod/) ||
    // iPad Air/Pro reports as Macintosh
    (matchUA(/Macintosh/) && navigator.maxTouchPoints > 1)
  );
}

function isDeviceAndroid(): boolean {
  return matchUA(/Android/);
}

function isIPad(): boolean {
  return (
    matchUA(/iPad/) ||
    (matchUA(/Macintosh/) && navigator.maxTouchPoints > 1)
  );
}

// iOS browsers
function isBrowserIOSChrome(): boolean {
  return isDeviceIOS() && matchUA(/CriOS/);
}
function isBrowserIOSFirefox(): boolean {
  return isDeviceIOS() && matchUA(/FxiOS/);
}
function isBrowserIOSInAppFacebook(): boolean {
  return isDeviceIOS() && matchUA(/FBAN|FBAV/);
}
function isBrowserIOSInAppLinkedin(): boolean {
  return isDeviceIOS() && matchUA(/LinkedInApp/);
}
function isBrowserIOSInAppInstagram(): boolean {
  if (!isDeviceIOS()) return false;
  // Instagram's iOS webview includes "Instagram" in the UA on newer versions
  if (matchUA(/Instagram/)) return true;
  // Fallback: check referrer (only works on first navigation)
  try {
    return !!document.referrer.match('//l.instagram.com/');
  } catch {
    return false;
  }
}
function isBrowserIOSInAppTwitter(): boolean {
  if (!isDeviceIOS()) return false;
  try {
    return !!document.referrer.match('//t.co/');
  } catch {
    return false;
  }
}
function isBrowserIOSSafari(): boolean {
  return (
    isDeviceIOS() &&
    matchUA(/Safari/) &&
    !isBrowserIOSChrome() &&
    !isBrowserIOSFirefox() &&
    !isBrowserIOSInAppFacebook() &&
    !isBrowserIOSInAppLinkedin() &&
    !isBrowserIOSInAppInstagram() &&
    !isBrowserIOSInAppTwitter()
  );
}
function isBrowserIOSSafari26(): boolean {
  if (!isBrowserIOSSafari()) return false;
  const m = navigator.userAgent.match(/Version\/(\d+)/);
  if (m) return parseInt(m[1]) >= 26;
  return false;
}

// Android browsers
function isBrowserAndroidChrome(): boolean {
  return (
    isDeviceAndroid() &&
    matchUA(/Chrome/) &&
    !matchUA(/FBAN|FBAV/) &&
    !matchUA(/Instagram/) &&
    !matchUA(/SamsungBrowser/) &&
    !matchUA(/Firefox/) &&
    !matchUA(/Edg/) &&
    !matchUA(/OPR/)
  );
}
function isBrowserAndroidFacebook(): boolean {
  return isDeviceAndroid() && matchUA(/FBAN|FBAV/);
}
function isBrowserAndroidInstagram(): boolean {
  return isDeviceAndroid() && matchUA(/Instagram/);
}
function isBrowserAndroidFirefox(): boolean {
  return isDeviceAndroid() && matchUA(/Firefox/);
}
function isBrowserAndroidEdge(): boolean {
  return isDeviceAndroid() && matchUA(/Edg/);
}

// Desktop browsers
function isDesktopChrome(): boolean {
  return (
    navigator.userAgent.includes('Chrome') &&
    !navigator.userAgent.includes('Edg') &&
    (navigator.userAgent.includes('Windows') ||
      navigator.userAgent.includes('Macintosh') ||
      navigator.userAgent.includes('Linux')) &&
    !isDeviceIOS() &&
    !isDeviceAndroid()
  );
}
function isDesktopEdge(): boolean {
  return (
    navigator.userAgent.includes('Edg/') &&
    !isDeviceIOS() &&
    !isDeviceAndroid()
  );
}
function isDesktopSafari(): boolean {
  return (
    navigator.userAgent.includes('Safari') &&
    !navigator.userAgent.includes('Chrome') &&
    !navigator.userAgent.includes('Edg') &&
    navigator.userAgent.includes('Macintosh') &&
    !isDeviceIOS()
  );
}

// Standalone detection — exportada porque `lib/pwaAnalytics.ts` la necesita
// fuera de React (y no puede duplicar la heurística: iOS no soporta la media
// query y hay que mirar el `navigator.standalone` propietario).
export function checkIsStandalone(): boolean {
  return (
    !!((navigator as any).standalone) ||
    window.matchMedia('(display-mode: standalone)').matches
  );
}

// ─── Resolve browser type ────────────────────────────────────────────────────

function detectBrowser(): BrowserType {
  if (isDeviceIOS()) {
    if (isBrowserIOSInAppFacebook()) return 'inapp-facebook';
    if (isBrowserIOSInAppInstagram()) return 'inapp-instagram';
    if (isBrowserIOSInAppTwitter()) return 'inapp-twitter';
    if (isBrowserIOSInAppLinkedin()) return 'inapp-linkedin';
    if (isBrowserIOSSafari26()) return 'safari-26';
    if (isBrowserIOSSafari()) return 'safari';
    if (isBrowserIOSChrome()) return 'chrome';
    if (isBrowserIOSFirefox()) return 'firefox';
    return 'unknown';
  }
  if (isDeviceAndroid()) {
    if (isBrowserAndroidFacebook()) return 'inapp-facebook';
    if (isBrowserAndroidInstagram()) return 'inapp-instagram';
    if (isBrowserAndroidChrome()) return 'chrome';
    if (isBrowserAndroidEdge()) return 'edge';
    if (isBrowserAndroidFirefox()) return 'firefox';
    return 'unknown';
  }
  // Desktop
  if (isDesktopChrome()) return 'chrome';
  if (isDesktopEdge()) return 'edge';
  if (isDesktopSafari()) return 'safari';
  return 'unknown';
}

function detectDevice(): DeviceType {
  if (isDeviceIOS()) return 'ios';
  if (isDeviceAndroid()) return 'android';
  return 'desktop';
}

// ─── Resolve install method ──────────────────────────────────────────────────

function resolveInstallMethod(device: DeviceType, browser: BrowserType): InstallMethod {
  // In-app browsers → must open in system browser first
  if (browser.startsWith('inapp-')) return 'open-in-browser';

  if (device === 'ios') {
    if (browser === 'safari-26') return 'ios-safari-26';
    if (browser === 'safari') return 'ios-safari';
    if (browser === 'chrome') return 'ios-chrome';
    return 'not-supported';
  }

  if (device === 'android') {
    if (browser === 'chrome') return 'android-chrome';
    if (browser === 'edge') return 'android-edge';
    if (browser === 'firefox') return 'android-firefox';
    return 'not-supported';
  }

  // Desktop
  if (browser === 'chrome' || browser === 'edge') return 'native-prompt';
  if (browser === 'safari') return 'desktop-safari';
  return 'not-supported';
}

// ─── Standalone helpers (no hook needed) ─────────────────────────────────────

/**
 * Returns true if the current browser is an in-app webview (Instagram, Facebook, Twitter, LinkedIn, Threads, etc.).
 * On iOS, also returns true for any unrecognised webview that isn't Safari/Chrome/Firefox,
 * since those webviews can't download files via blob URLs.
 */
export function isInAppBrowser(): boolean {
  const browser = detectBrowser();
  if (browser.startsWith('inapp-')) return true;
  // On iOS, any "unknown" browser is almost certainly an in-app webview
  if (isDeviceIOS() && browser === 'unknown') return true;
  return false;
}

// ─── `beforeinstallprompt` — singleton compartido ────────────────────────────
//
// El evento se dispara UNA sola vez por carga de página y se pierde si nadie lo
// guarda. Antes cada instancia del hook se lo apropiaba en un ref y BORRABA el
// global `window.__pwaInstallPrompt` (que setea `main.tsx`): la primera
// instancia en montar (la de `Settings`) se lo llevaba y las demás — entre
// ellas la de `AddToHomescreenPrompt`, que es la que necesita dispararlo —
// nunca veían el prompt nativo. Por eso ahora vive a nivel de módulo y las
// instancias se suscriben a los cambios. Ver ADR-679.

let deferredPrompt: any = null;
const promptSubscribers = new Set<(has: boolean) => void>();

function publishPrompt(e: any): void {
  deferredPrompt = e;
  promptSubscribers.forEach((fn) => {
    try {
      fn(!!e);
    } catch {
      /* noop */
    }
  });
}

if (typeof window !== 'undefined') {
  // Este listener se registra al evaluar el módulo — es decir ANTES del cuerpo
  // de `main.tsx` (los imports se evalúan primero), así que cubre el caso
  // normal. El de `main.tsx` queda como red de seguridad.
  window.addEventListener('beforeinstallprompt', (e: Event) => {
    e.preventDefault();
    publishPrompt(e);
  });
  // Y si el evento llegó antes de que este módulo existiera (orden de imports
  // distinto, HMR), lo adoptamos del global.
  const early = (window as any).__pwaInstallPrompt;
  if (early) {
    delete (window as any).__pwaInstallPrompt;
    publishPrompt(early);
  }
  // Instalada → el prompt ya no sirve.
  window.addEventListener('appinstalled', () => publishPrompt(null));
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useDeviceDetection(): DeviceDetectionResult {
  const device = detectDevice();
  const browser = detectBrowser();
  const isStandalone = checkIsStandalone();
  const iPad = isIPad();
  const installMethod = resolveInstallMethod(device, browser);
  const canInstall = installMethod !== 'not-supported' && !isStandalone;

  // beforeinstallprompt for Desktop Chrome/Edge — leído del singleton de módulo
  const [hasNativePrompt, setHasNativePrompt] = useState<boolean>(
    () => !!deferredPrompt,
  );

  useEffect(() => {
    const sub = (has: boolean) => setHasNativePrompt(has);
    promptSubscribers.add(sub);
    // Puede haber llegado entre el render y este efecto.
    setHasNativePrompt(!!deferredPrompt);
    return () => {
      promptSubscribers.delete(sub);
    };
  }, []);

  const triggerNativeInstall = useCallback(async (): Promise<boolean> => {
    const prompt = deferredPrompt;
    if (!prompt) return false;
    try {
      prompt.prompt();
      const result = await prompt.userChoice;
      // El evento es de un solo uso: `prompt()` no se puede volver a llamar.
      publishPrompt(null);
      return result?.outcome === 'accepted';
    } catch {
      publishPrompt(null);
      return false;
    }
  }, []);

  return {
    device,
    browser,
    isStandalone,
    isIPad: iPad,
    installMethod,
    canInstall,
    triggerNativeInstall,
    hasNativePrompt,
  };
}
